import { useEffect, useState } from 'react';

export type Options = {
    perPage: number;
    zeroShift?: number;
    loop?: boolean;
    withScrollUp?: boolean;
    step?: number;
};

export type PaginationOptions = {
    visibleItems: any[];
    getPageByIndex: (index: number) => any[];
    perPage: number;
    currentPage: number;
    pagesCount: number;
    totalCount: number;
    handlePerPageChange: (newValue: number) => void;
    handleNextPage: () => void;
    handlePrevPage: () => void;
    handlePageNavigation: (newValue: number) => void;
    isFirstPage: boolean;
    isLastPage: boolean;
};

const usePagination = <T>(items: T[] = [], options: Options): PaginationOptions => {
    const { zeroShift = 0, loop = false, withScrollUp = false } = options ?? {};

    const [perPage, setPerPage] = useState(1);
    const [currentPage, setCurrentPage] = useState(0);

    const totalCount = items?.length;
    const pagesCount = Math.ceil(totalCount / (perPage || 1));

    useEffect(() => {
        setPerPage((options?.perPage ?? 1) || 1);
    }, [options?.perPage]);

    useEffect(() => {
        if (withScrollUp) {
            try {
                (document as any).getElementById(`scrollWrapper`).scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {}
            try {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {}
        }
    }, [currentPage]);

    useEffect(() => {
        setCurrentPage(Math.max(Math.min(currentPage, pagesCount - 1), 0));
    }, [perPage]);

    return {
        visibleItems: items?.slice?.(perPage * currentPage, perPage * (currentPage + 1)) ?? [],
        getPageByIndex: (index: number) => {
            return items?.slice?.(perPage * index, perPage * (index + 1)) ?? [];
        },
        perPage,
        currentPage: currentPage + zeroShift,
        pagesCount,
        totalCount: items?.length,
        handlePerPageChange: (newValue: number) => {
            setPerPage(Math.max(newValue, 0));
        },
        handleNextPage: () => {
            if (loop) {
                setCurrentPage((currentPage + 1) % pagesCount);
            } else {
                setCurrentPage(Math.min(currentPage + 1, pagesCount - 1));
            }
        },
        handlePrevPage: () => {
            if (loop) {
                setCurrentPage((currentPage - 1 + pagesCount) % pagesCount);
            } else {
                setCurrentPage(Math.max(currentPage - 1, 0));
            }
        },
        handlePageNavigation: (newValue: number) => {
            setCurrentPage(Math.min(Math.max(newValue - zeroShift, 0), pagesCount));
        },
        isFirstPage: currentPage === 0,
        isLastPage: currentPage === pagesCount - 1,
    };
};

export default usePagination;
